<script lang="ts">
  import BannerDisplay from "@shared/svelte/Components/Banner/BannerDisplay.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import {
    BannerEntity,
    BannerAdEntity,
    CarrotBannerAdEntity,
    CustomContentBannerEntity,
  } from "@shared/services/event_tracking/entities";
  import { getCloudinaryUrl } from "@shared/services/cloudinary_image_url_service";
  import { cloudinaryParams } from "@shared/services/discovery_image_dimensions";

  import type { Banner } from "@shared/types/Banner";

  export let banners: Banner[];
  export let mobileMode: boolean;
  export let pageName: string | null = null;

  // Unused props passed from discovery panel
  export let type: string = null;
  type;

  const extractDimensions = (slotId: string): number[] | null => {
    const regexpSize = /([0-9]+)x([0-9]+)/;
    const match = slotId.match(regexpSize);
    if (!match) {
      return;
    }

    return [Number(match[1]), Number(match[2])];
  };

  const getImageParams = (bannerWidth: number, slotIdString: string) => {
    if (bannerWidth === 3200) {
      return slotIdString === "desktop"
        ? cloudinaryParams.desktopBanner
        : cloudinaryParams.mobileBanner;
    } else {
      return slotIdString === "desktop"
        ? cloudinaryParams.legacyDesktopBanner
        : cloudinaryParams.legacyMobileBanner;
    }
  };

  interface ParsedBanner {
    banner: Banner;
    entities: (BannerEntity | CarrotBannerAdEntity)[];
  }
  let isCarrotAd: boolean = false;
  const parseBanner = (bannerRes, slotIdString: string): ParsedBanner | null => {
    const banner = bannerRes?.find(l => l.slot_id?.toLowerCase().includes(slotIdString));

    if (!banner) {
      return;
    }

    const dimensions = extractDimensions(banner.slot_id);
    if (!dimensions) {
      return;
    } else {
      // Slot ids must have WxH as part of their naming convention
      banner.width = dimensions[0];
      banner.height = dimensions[1];
    }

    let entities: any[] = [BannerEntity.fromBanner(banner)];

    if (banner.carrot_ad_format) {
      entities = entities.concat([new CarrotBannerAdEntity(banner)]);
      isCarrotAd = true;
    }

    if (banner.campaign_id) {
      entities = entities.concat([new BannerAdEntity(banner)]);
    }

    if (banner.custom_content_banner_id) {
      entities = entities.concat([
        new CustomContentBannerEntity({
          id: banner.custom_content_banner_id,
          position: banner.position,
        }),
      ]);
    }

    if (!banner.carrot_ad_format) {
      // When banner is from carrot, we will already have the full URL for the image.
      // However, when banner is from an internal record, the image will be hosted in
      // Cloudinary and only partial path is provided via the image_url field.

      const imageParams = getImageParams(banner.width, slotIdString);
      banner.image_url = getCloudinaryUrl(banner.image_url, imageParams);
    }

    return {
      banner,
      entities,
    };
  };

  const desktopBanner = parseBanner(banners, "desktop");
  const mobileBanner = parseBanner(banners, "mobile");

  const extraMargin = !["product_detail", "lists"].includes(pageName) && !isCarrotAd;
</script>

<style>
  .extra-padding {
    padding-inline: 1.5rem;
  }
</style>

<div
  data-testid="banner"
  class="fizz-container"
  class:extra-padding={["home", "custom_content"].includes(pageName)}
  class:fizz-stack-24={extraMargin}
>
  {#if mobileBanner && mobileMode === true}
    <TrackingContext entities={mobileBanner.entities}>
      <BannerDisplay banner={mobileBanner.banner} testId={"banner-mobile"} />
    </TrackingContext>
  {/if}

  {#if desktopBanner && mobileMode === false}
    <TrackingContext entities={desktopBanner.entities}>
      <BannerDisplay banner={desktopBanner.banner} testId={"banner-desktop"} />
    </TrackingContext>
  {/if}
</div>
